import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ORANGE, LONG_PRESS_DELAY } from "../constants";

export default function ShiftChip({
  shift,
  collab,
  teamId,
  isWeekly,
  color,
  label,
  timeDisplay,
  isSelected,
  isHolding,
  canDrag,
  dragActiveRef,
  onPointerDown,
  onPointerUp,
  onClick,
  dynamicLeaves,
}) {
  const isLeaveType = !!shift.id_sample_leave || shift.type === "LEAVE" || !!dynamicLeaves[shift.type];
  const isDelegated =
    shift.id_sample_shift &&
    shift.id_team_delegate &&
    shift.id_team_delegate !== teamId;

  const chipBg = isLeaveType || ["R", "LEAVE"].includes(shift.type)
    ? (isWeekly ? "#fff" : `${color}35`)
    : color;

  const borderColor =
    ["R", "LEAVE"].includes(shift.type) ? "rgba(0,0,0,0.3)" : "transparent";

  const delegatedBg = isDelegated
    ? `repeating-linear-gradient(-45deg, ${chipBg}, ${chipBg} 3px, rgba(255,255,255,0.6) 3px, rgba(255,255,255,0.6) 6px)`
    : undefined;

  return (
    <Box
      key={shift.id}
      onPointerDown={(e) => {
        e.stopPropagation();
        onPointerDown(e, shift, collab, teamId);
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        onPointerUp();
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!dragActiveRef.current) {
          onClick(shift);
        }
      }}
      onContextMenu={(e) => e.preventDefault()}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: isWeekly ? 28 : 20,
        maxHeight: isWeekly ? 28 : 20,
        borderRadius: isWeekly ? "6px" : "3px",
        px: isWeekly ? 0.5 : 0.25,
        mb: "2px",
        cursor: canDrag ? "grab" : "pointer",
        touchAction: "none",
        overflow: "hidden",
        border: `1px solid ${borderColor}`,
        backgroundColor: isDelegated ? "transparent" : chipBg,
        background: delegatedBg || undefined,
        opacity: isSelected ? 0.4 : 1,
        transition: isHolding
          ? `transform ${LONG_PRESS_DELAY}ms ease, box-shadow ${LONG_PRESS_DELAY}ms ease, outline ${LONG_PRESS_DELAY}ms ease`
          : "transform 0.12s ease",
        transform: isHolding ? "scale(1.08)" : "scale(1)",
        boxShadow: isHolding ? `0 0 0 3px ${ORANGE}` : "none",
        "&:active": { transform: "scale(0.95)" },
        ...(isLeaveType && { borderLeft: `3px solid ${color}` }),
      }}
    >
      {isWeekly ? (
        <Typography
          variant="caption"
          noWrap
          sx={{
            fontSize: "0.6rem",
            lineHeight: 1.2,
            fontWeight: 600,
            color: isLeaveType || ["R", "LEAVE"].includes(shift.type)
              ? "text.primary"
              : "#fff",
            textShadow:
              isLeaveType || ["R", "LEAVE"].includes(shift.type)
                ? "none"
                : "0 1px 2px rgba(0,0,0,0.3)",
            width: "100%",
            textAlign: "center",
          }}
        >
          {timeDisplay || label || shift.type}
        </Typography>
      ) : (
        <Box sx={{ width: "100%", height: "100%" }} />
      )}

      {/* Selected overlay */}
      {isSelected && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            borderRadius: "inherit",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Status indicator */}
      {shift.id_sample_leave && shift.status === "VALIDATED" && isWeekly && (
        <CheckCircleIcon
          sx={{
            position: "absolute",
            top: -2,
            right: -2,
            fontSize: 10,
            color: "green",
            backgroundColor: "#fff",
            borderRadius: "50%",
          }}
        />
      )}
      {shift.id_sample_leave &&
        (shift.status === "PENDING" || shift.status === "DELETE_REQUESTED") &&
        isWeekly && (
          <AccessTimeIcon
            sx={{
              position: "absolute",
              top: -2,
              right: -2,
              fontSize: 10,
              color: ORANGE,
              backgroundColor: "#fff",
              borderRadius: "50%",
            }}
          />
        )}
    </Box>
  );
}
